import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { GuideThumb, GoogleSearchLink } from './ExerciseGuidePreview';
import { findGuideExercise } from './exerciseGuide';
import translations from './translations';

function ExerciseGuideHint({ name, language, onOpen }) {
  const tt = translations[language] || translations.en;
  if (!name?.trim()) return null;
  if (findGuideExercise(name)) {
    return <GuideThumb name={name} onOpen={onOpen} size={40} />;
  }
  return (
    <GoogleSearchLink name={name} language={language} className="btn btn-dark program-ex-google">
      {tt.searchGoogle}
    </GoogleSearchLink>
  );
}

export default function ProgramBuilder({ onProgramConfirmed, language, user, initialProgram, onCancelEdit, onOpenExercise }) {
  const t = {
    goal: language === 'da' ? 'Hvad er dit primære mål?' : 'What is your main goal?',
    days: language === 'da' ? 'Hvor mange dage om ugen kan du træne?' : 'How many days per week can you train?',
    experience: language === 'da' ? 'Hvad er dit erfaringsniveau?' : 'What is your experience level?',
    equipment: language === 'da' ? 'Hvilket udstyr har du adgang til?' : 'What equipment do you have access to?',
    preference: language === 'da' ? 'Hvad er din træningspræference?' : 'What is your training preference?',
    next: language === 'da' ? 'Næste →' : 'Next →',
    generate: language === 'da' ? 'Generér mit program' : 'Generate my program',
    generating: language === 'da' ? 'Bygger dit program...' : 'Building your program...',
    reroll: language === 'da' ? 'Prøv igen' : 'Re-roll',
    confirm: language === 'da' ? 'Bekræft og brug dette program' : 'Confirm and use this program',
    startOver: language === 'da' ? '← Start forfra' : '← Start Over',
    restDay: language === 'da' ? 'Hvile & Restitution' : 'Rest & Recovery',
    error: language === 'da' ? 'Fejl ved generering af program. Prøv igen!' : 'Error generating program. Please try again!',
    manualCreate: language === 'da' ? 'Opret program manuelt' : 'Create program manually',
    manualDaysTitle: language === 'da' ? 'Hvor mange dage har dit program?' : 'How many days in your program?',
    dayName: language === 'da' ? 'Dagnavn' : 'Day name',
    dayNamePlaceholder: language === 'da' ? 'f.eks. Dag 1 – Push' : 'e.g. Day 1 – Push',
    restDayLabel: language === 'da' ? 'Hviledag' : 'Rest day',
    exercises: language === 'da' ? 'Øvelser' : 'Exercises',
    addExercise: language === 'da' ? '+ Tilføj øvelse' : '+ Add exercise',
    exercisePlaceholder: language === 'da' ? 'Øvelsesnavn' : 'Exercise name',
    setsRepsPlaceholder: language === 'da' ? 'f.eks. 3 x 8-12' : 'e.g. 3 x 8-12',
    remove: language === 'da' ? 'Fjern' : 'Remove',
    backToAI: language === 'da' ? '← Tilbage til AI-program' : '← Back to AI program',
    cancelEdit: language === 'da' ? '← Annuller redigering' : '← Cancel edit',
    editTitle: language === 'da' ? 'Rediger dit program' : 'Edit your program',
    repsLabel: 'Reps:',
  };

  const goals = language === 'da' ? [
    { key: 'muscle', label: '💪 Muskelopbygning', desc: 'Byg størrelse og styrke' },
    { key: 'weightloss', label: '🔥 Vægttab', desc: 'Forbrænd fedt, bevar styrke' },
    { key: 'strength', label: '🏋️ Styrke', desc: 'Bliv stærkere, løft tungere' },
  ] : [
    { key: 'muscle', label: '💪 Muscle Building', desc: 'Build size and strength' },
    { key: 'weightloss', label: '🔥 Weight Loss', desc: 'Burn fat, stay strong' },
    { key: 'strength', label: '🏋️ Strength', desc: 'Get stronger, lift heavier' },
  ];

  const daysOptions = ['2', '3', '4', '5', '6'];
  const experienceOptions = language === 'da' ? ['Begynder (0-1 år)', 'Øvet (1-3 år)', 'Avanceret (3+ år)'] : ['Beginner (0-1 years)', 'Intermediate (1-3 years)', 'Advanced (3+ years)'];
  const equipmentOptions = language === 'da' ? ['Fuldt fitnesscenter', 'Kun håndvægte', 'Vægtstang + rack', 'Kun kropsvægt'] : ['Full gym', 'Dumbbells only', 'Barbell + rack', 'Bodyweight only'];
  const preferenceOptions = language === 'da' ? ['Mange gentagelser, lettere vægt', 'Få gentagelser, tung vægt', 'Blanding af begge'] : ['High reps, lower weight', 'Low reps, heavy weight', 'Mix of both'];

  const defaultManualDays = [
    { dayName: 'Day 1', isRest: false, exercises: [{ name: '', setsReps: '' }] },
    { dayName: 'Day 2', isRest: false, exercises: [{ name: '', setsReps: '' }] },
  ];

  function mapInitialProgram(days) {
    return days.map((d) => ({
      dayName: d.dayName || '',
      isRest: d.isRest || false,
      exercises: d.exercises?.length
        ? d.exercises.map((ex) => ({ name: ex.name || '', setsReps: ex.setsReps || '' }))
        : [{ name: '', setsReps: '' }],
    }));
  }

  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState('');
  const [days, setDays] = useState('4');
  const [experience, setExperience] = useState('');
  const [equipment, setEquipment] = useState('');
  const [preference, setPreference] = useState('');
  const [program, setProgram] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsedDays, setParsedDays] = useState([]);
  const [editingDay, setEditingDay] = useState(null);
  const [editingExIdx, setEditingExIdx] = useState(null);
  const [newExName, setNewExName] = useState('');
  const [newExSetsReps, setNewExSetsReps] = useState('');
  const [draggedDayIndex, setDraggedDayIndex] = useState(null);
  const [dropTargetDayIndex, setDropTargetDayIndex] = useState(null);
  const [draggedManualDayIndex, setDraggedManualDayIndex] = useState(null);
  const [dropTargetManualDayIndex, setDropTargetManualDayIndex] = useState(null);
  const [manualMode, setManualMode] = useState(!!initialProgram);
  const [manualDays, setManualDays] = useState(() =>
    initialProgram?.length ? mapInitialProgram(initialProgram) : defaultManualDays
  );

  async function generateProgram() {
    setLoading(true);
    setProgram('');
    setParsedDays([]);
    const prompt = `You are an expert personal trainer. Create a ${days}-day per week workout program for:
- Goal: ${goal}
- Experience: ${experience}
- Equipment: ${equipment}
- Preference: ${preference}
Respond in ${language === 'da' ? 'Danish' : 'English'}. IMPORTANT: Always write exercise names in English regardless of language (e.g. "Bench Press" not "Bænkpres").

IMPORTANT: Format your response in two parts:

PART 1 - PROGRAM OVERVIEW:
Write a short motivating intro (2-3 sentences) about the program philosophy.

PART 2 - WORKOUT DAYS (use EXACTLY this format for each day, no exceptions):
DAY_START
Day: [Day number and name, e.g. "Day 1 - Push"]
Exercises:
- [Exercise name] | [sets] sets x [reps] reps | Tip: [one sentence tip]
- [Exercise name] | [sets] sets x [reps] reps | Tip: [one sentence tip]
DAY_END

Repeat DAY_START...DAY_END for each training day. Rest days should just say "Rest Day" after Day: label with no exercises.`;

    try {
      const idToken = user ? await user.getIdToken() : null;
      if (!idToken) throw new Error('Missing auth token');

      const res = await fetch(process.env.REACT_APP_API_URL || '/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ prompt })
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      const raw = data.result;
      setProgram(raw);
      setParsedDays(parseProgramDays(raw));
      setStep(5);
    } catch (err) {
      setProgram(t.error);
      setStep(5);
    }
    setLoading(false);
  }

  function parseProgramDays(raw) {
    const dayBlocks = raw.split('DAY_START').slice(1);
    return dayBlocks.map(block => {
      const content = block.split('DAY_END')[0].trim();
      const lines = content.split('\n').filter(l => l.trim());
      const dayLine = lines.find(l => l.startsWith('Day:'));
      const dayName = dayLine ? dayLine.replace('Day:', '').trim() : 'Day';
      const isRest = content.toLowerCase().includes('rest day') || content.toLowerCase().includes('hviledag');
      const exercises = isRest ? [] : lines
        .filter(l => l.startsWith('-'))
        .map(l => {
          const clean = l.replace(/^-\s*/, '');
          const parts = clean.split('|');
          return {
            name: parts[0]?.trim() || clean,
            setsReps: parts[1]?.trim() || '',
            tip: parts[2]?.replace('Tip:', '').trim() || '',
          };
        });
      return { dayName, isRest, exercises };
    });
  }

  function reorderDays(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    const newOrder = [...parsedDays];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setParsedDays(newOrder);
    setDropTargetDayIndex(null);
  }

  function reorderManualDays(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    const newOrder = [...manualDays];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setManualDays(newOrder);
    setDropTargetManualDayIndex(null);
  }

  function saveExerciseEdit(dayIdx, exIdx) {
    if (!newExName.trim()) return;
    const updated = parsedDays.map((day, di) => {
      if (di !== dayIdx) return day;
      return {
        ...day,
        exercises: day.exercises.map((ex, ei) =>
          ei !== exIdx ? ex : { ...ex, name: newExName.trim(), setsReps: (newExSetsReps || '').trim() }
        )
      };
    });
    setParsedDays(updated);
    setEditingDay(null);
    setEditingExIdx(null);
    setNewExName('');
    setNewExSetsReps('');
  }

  function confirmProgram() {
    if (onProgramConfirmed) onProgramConfirmed(parsedDays);
  }

  const optionStyle = (active) => ({
    background: active ? 'var(--accent)' : 'var(--surface2)',
    border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
    color: active ? 'white' : 'var(--text)',
    padding: '12px 16px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: 'Barlow, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    textAlign: 'left',
    width: '100%',
    marginBottom: '8px',
    transition: 'all 0.2s',
  });

  const nextBtn = (disabled) => (
    <button className="btn btn-primary" disabled={disabled} onClick={() => setStep(step + 1)}
      style={{ marginTop: '16px', padding: '10px 24px', opacity: disabled ? 0.4 : 1 }}>
      {t.next}
    </button>
  );

  const reset = () => {
    setStep(0); setGoal(''); setProgram(''); setExperience('');
    setEquipment(''); setPreference(''); setParsedDays([]);
    setManualMode(false);
    setManualDays([
      { dayName: 'Day 1', isRest: false, exercises: [{ name: '', setsReps: '' }] },
      { dayName: 'Day 2', isRest: false, exercises: [{ name: '', setsReps: '' }] },
    ]);
  };

  function setManualDaysCount(n) {
    const count = Math.max(2, Math.min(7, parseInt(n, 10) || 2));
    setManualDays(prev => {
      if (prev.length === count) return prev;
      if (prev.length < count) {
        return [...prev, ...Array(count - prev.length).fill(null).map((_, i) => ({
          dayName: language === 'da' ? `Dag ${prev.length + i + 1}` : `Day ${prev.length + i + 1}`,
          isRest: false,
          exercises: [{ name: '', setsReps: '' }],
        }))];
      }
      return prev.slice(0, count);
    });
  }

  function updateManualDay(dayIdx, field, value) {
    setManualDays(prev => prev.map((d, i) => i !== dayIdx ? d : { ...d, [field]: value }));
  }

  function updateManualExercise(dayIdx, exIdx, field, value) {
    setManualDays(prev => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      return { ...d, exercises: d.exercises.map((ex, j) => j !== exIdx ? ex : { ...ex, [field]: value }) };
    }));
  }

  function addManualExercise(dayIdx) {
    setManualDays(prev => prev.map((d, i) => i !== dayIdx ? d : { ...d, exercises: [...d.exercises, { name: '', setsReps: '' }] }));
  }

  function removeManualExercise(dayIdx, exIdx) {
    setManualDays(prev => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      const exs = d.exercises.filter((_, j) => j !== exIdx);
      return { ...d, exercises: exs.length ? exs : [{ name: '', setsReps: '' }] };
    }));
  }

  function confirmManualProgram() {
    const days = manualDays.map(d => ({
      dayName: d.dayName.trim() || (language === 'da' ? 'Dag' : 'Day'),
      isRest: d.isRest,
      exercises: d.exercises.filter(ex => ex.name.trim()).map(ex => ({ name: ex.name.trim(), setsReps: (ex.setsReps || '').trim(), tip: '' })),
    }));
    if (onProgramConfirmed) onProgramConfirmed(days);
  }

  const canConfirmManual = manualDays.some(d => !d.isRest && d.exercises.some(ex => ex.name.trim()));

  return (
    <div>
      {loading && (
        <div className="ai-loading">
          <div className="ai-loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="ai-loading-text">{t.generating}</div>
        </div>
      )}

      {!loading && step === 0 && !manualMode && (
        <>
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '14px' }}>{t.goal}</p>
          {goals.map(g => (
            <button key={g.key} style={optionStyle(goal === g.key)} onClick={() => setGoal(g.key)}>
              <div>{g.label}</div>
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>{g.desc}</div>
            </button>
          ))}
          {nextBtn(!goal)}
          <button className="btn btn-dark" style={{ width: '100%', marginTop: '16px' }} onClick={() => setManualMode(true)}>
            {t.manualCreate}
          </button>
        </>
      )}

      {!loading && manualMode && (
        <>
          {initialProgram && (
            <p style={{ color: 'var(--text-muted)', marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>{t.editTitle}</p>
          )}
          {!initialProgram && (
            <p style={{ color: 'var(--text-muted)', marginBottom: '12px', fontSize: '14px' }}>{t.manualDaysTitle}</p>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {[2, 3, 4, 5, 6, 7].map(n => (
              <button
                key={n}
                style={{ ...optionStyle(manualDays.length === n), width: 'auto', padding: '10px 16px' }}
                onClick={() => setManualDaysCount(n)}
              >
                {n} {language === 'da' ? 'dage' : 'days'}
              </button>
            ))}
          </div>

          {manualDays.map((day, dayIdx) => (
            <div
              key={dayIdx}
              data-index={dayIdx}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (draggedManualDayIndex !== null) setDropTargetManualDayIndex(dayIdx); }}
              onDragLeave={() => setDropTargetManualDayIndex(null)}
              onDrop={(e) => { e.preventDefault(); const toIndex = parseInt(e.currentTarget.dataset.index, 10); const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10); if (fromIndex !== toIndex) reorderManualDays(fromIndex, toIndex); setDraggedManualDayIndex(null); setDropTargetManualDayIndex(null); }}
              style={{
                background: 'var(--surface2)',
                border: '2px solid',
                borderColor: dropTargetManualDayIndex === dayIdx && draggedManualDayIndex !== null ? 'var(--accent)' : 'var(--border)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                opacity: draggedManualDayIndex === dayIdx ? 0.6 : 1,
                boxShadow: dropTargetManualDayIndex === dayIdx && draggedManualDayIndex !== null ? '0 0 0 2px var(--accent-soft)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(dayIdx)); e.dataTransfer.effectAllowed = 'move'; setDraggedManualDayIndex(dayIdx); }}
                  onDragEnd={() => { setDraggedManualDayIndex(null); setDropTargetManualDayIndex(null); }}
                  style={{ color: 'var(--text-muted)', fontSize: '18px', cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
                  aria-label={language === 'da' ? 'Træk for at flytte dag' : 'Drag to reorder day'}
                  title={language === 'da' ? 'Træk for at flytte dag' : 'Drag to reorder day'}
                >⋮⋮</span>
                <p style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', margin: 0 }}>
                  {language === 'da' ? 'Dag' : 'Day'} {dayIdx + 1}
                </p>
              </div>
              <input
                type="text"
                value={day.dayName}
                onChange={e => updateManualDay(dayIdx, 'dayName', e.target.value)}
                placeholder={t.dayNamePlaceholder}
                className="input-field wide"
                style={{ marginBottom: '10px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={day.isRest} onChange={e => updateManualDay(dayIdx, 'isRest', e.target.checked)} />
                {t.restDayLabel}
              </label>
              {!day.isRest && (
                <>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.exercises}</p>
                  {day.exercises.map((ex, exIdx) => (
                    <div key={exIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <ExerciseGuideHint name={ex.name} language={language} onOpen={onOpenExercise} />
                      <input
                        type="text"
                        value={ex.name}
                        onChange={e => updateManualExercise(dayIdx, exIdx, 'name', e.target.value)}
                        placeholder={t.exercisePlaceholder}
                        className="input-field wide"
                        style={{ flex: '1 1 140px', marginBottom: 0 }}
                      />
                      <input
                        type="text"
                        value={ex.setsReps || ''}
                        onChange={e => updateManualExercise(dayIdx, exIdx, 'setsReps', e.target.value)}
                        placeholder={t.setsRepsPlaceholder}
                        className="input-field"
                        style={{ width: '100px', marginBottom: 0, flexShrink: 0 }}
                      />
                      <button type="button" className="btn-icon" onClick={() => removeManualExercise(dayIdx, exIdx)} style={{ flexShrink: 0 }} aria-label={t.remove}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-dark" style={{ fontSize: '13px', padding: '8px 14px' }} onClick={() => addManualExercise(dayIdx)}>
                    {t.addExercise}
                  </button>
                </>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            {initialProgram ? (
              <button type="button" className="btn btn-dark" style={{ flex: 1 }} onClick={onCancelEdit}>
                {t.cancelEdit}
              </button>
            ) : (
              <button type="button" className="btn btn-dark" style={{ flex: 1 }} onClick={() => setManualMode(false)}>
                {t.backToAI}
              </button>
            )}
            <button type="button" className="btn btn-primary" style={{ flex: 2 }} onClick={confirmManualProgram} disabled={!canConfirmManual}>
              {t.confirm}
            </button>
          </div>
          {!initialProgram && (
            <button type="button" className="btn btn-dark" style={{ width: '100%', marginTop: '8px' }} onClick={reset}>
              {t.startOver}
            </button>
          )}
        </>
      )}

      {!loading && step === 1 && (
        <>
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '14px' }}>{t.days}</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {daysOptions.map(d => (
              <button key={d} style={{ ...optionStyle(days === d), width: 'auto', padding: '10px 20px' }} onClick={() => setDays(d)}>
                {d} {language === 'da' ? 'dage' : 'days'}
              </button>
            ))}
          </div>
          {nextBtn(!days)}
        </>
      )}

      {!loading && step === 2 && (
        <>
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '14px' }}>{t.experience}</p>
          {experienceOptions.map(e => (
            <button key={e} style={optionStyle(experience === e)} onClick={() => setExperience(e)}>{e}</button>
          ))}
          {nextBtn(!experience)}
        </>
      )}

      {!loading && step === 3 && (
        <>
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '14px' }}>{t.equipment}</p>
          {equipmentOptions.map(e => (
            <button key={e} style={optionStyle(equipment === e)} onClick={() => setEquipment(e)}>{e}</button>
          ))}
          {nextBtn(!equipment)}
        </>
      )}

      {!loading && step === 4 && (
        <>
          <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '14px' }}>{t.preference}</p>
          {preferenceOptions.map(p => (
            <button key={p} style={optionStyle(preference === p)} onClick={() => setPreference(p)}>{p}</button>
          ))}
          <button className="btn btn-primary" disabled={!preference} onClick={generateProgram}
            style={{ marginTop: '16px', padding: '10px 24px', width: '100%', opacity: !preference ? 0.4 : 1 }}>
            {t.generate}
          </button>
        </>
      )}

      {!loading && step === 5 && (
        <>
          {parsedDays.length > 0 ? (
            <>
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '16px', fontSize: '14px', lineHeight: '1.7' }}>
                <ReactMarkdown>{program.split('DAY_START')[0]}</ReactMarkdown>
              </div>

              {parsedDays.map((day, di) => (
                <div
                  key={di}
                  data-index={di}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (draggedDayIndex !== null) setDropTargetDayIndex(di); }}
                  onDragLeave={() => setDropTargetDayIndex(null)}
                  onDrop={(e) => { e.preventDefault(); const toIndex = parseInt(e.currentTarget.dataset.index, 10); const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10); if (fromIndex !== toIndex) reorderDays(fromIndex, toIndex); setDraggedDayIndex(null); setDropTargetDayIndex(null); }}
                  style={{
                    background: 'var(--surface2)',
                    border: '2px solid',
                    borderColor: dropTargetDayIndex === di && draggedDayIndex !== null ? 'var(--accent)' : 'var(--border)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '10px',
                    opacity: draggedDayIndex === di ? 0.6 : 1,
                    boxShadow: dropTargetDayIndex === di && draggedDayIndex !== null ? '0 0 0 2px var(--accent-soft)' : 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(di)); e.dataTransfer.effectAllowed = 'move'; setDraggedDayIndex(di); }}
                      onDragEnd={() => { setDraggedDayIndex(null); setDropTargetDayIndex(null); }}
                      style={{ color: 'var(--text-muted)', fontSize: '18px', cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
                      aria-label={language === 'da' ? 'Træk for at flytte dag' : 'Drag to reorder day'}
                      title={language === 'da' ? 'Træk for at flytte dag' : 'Drag to reorder day'}
                    >⋮⋮</span>
                    <p style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.1rem', fontWeight: 900, textTransform: 'uppercase', color: 'var(--accent)', margin: 0 }}>
                      {day.dayName}
                    </p>
                  </div>
                  {day.isRest ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{t.restDay}</p>
                  ) : (
                    day.exercises.map((ex, ei) => (
                      <div key={ei} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '10px' }}>
                        {editingDay === di && editingExIdx === ei ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <ExerciseGuideHint name={newExName} language={language} onOpen={onOpenExercise} />
                              <input
                                value={newExName}
                                onChange={e => setNewExName(e.target.value)}
                                placeholder={language === 'da' ? 'Øvelsesnavn' : 'Exercise name'}
                                className="input-field wide"
                                style={{ marginBottom: 0 }}
                              />
                            </div>
                            <input
                              value={newExSetsReps}
                              onChange={e => setNewExSetsReps(e.target.value)}
                              placeholder={t.setsRepsPlaceholder}
                              className="input-field"
                              style={{ width: '100%', maxWidth: '200px', marginBottom: 0 }}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn btn-success" style={{ padding: '6px 12px' }} onClick={() => saveExerciseEdit(di, ei)}>✓</button>
                              <button className="btn btn-dark" style={{ padding: '6px 12px' }} onClick={() => { setEditingDay(null); setEditingExIdx(null); setNewExName(''); setNewExSetsReps(''); }}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', minWidth: 0 }}>
                              <ExerciseGuideHint name={ex.name} language={language} onOpen={onOpenExercise} />
                              <div>
                                <p style={{ fontWeight: 700, fontSize: '14px', marginBottom: '2px' }}>{ex.name}</p>
                                <p style={{ fontSize: '13px', color: 'var(--accent)' }}>{t.repsLabel} {ex.setsReps}</p>
                                {ex.tip && <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', fontStyle: 'italic' }}>💡 {ex.tip}</p>}
                              </div>
                            </div>
                            <button className="btn-icon" onClick={() => { setEditingDay(di); setEditingExIdx(ei); setNewExName(ex.name); setNewExSetsReps(ex.setsReps || ''); }}
                              style={{ fontSize: '14px', color: 'var(--text-muted)' }}>✏️</button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => { setStep(4); generateProgram(); }}>
                  {t.reroll}
                </button>
                <button className="btn btn-primary" style={{ flex: 2 }} onClick={confirmProgram}>
                  {t.confirm}
                </button>
              </div>
              <button className="btn btn-dark" style={{ width: '100%', marginTop: '8px' }} onClick={reset}>
                {t.startOver}
              </button>
            </>
          ) : (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--accent)', borderRadius: '12px', padding: '16px', fontSize: '14px', lineHeight: '1.7' }}>
              <ReactMarkdown>{program}</ReactMarkdown>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => { setStep(4); generateProgram(); }}>{t.reroll}</button>
                <button className="btn btn-dark" style={{ flex: 1 }} onClick={reset}>{t.startOver}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}